import {absolute, isValidArrayIndex, join, parse} from './jsonPointer';

const UUID_REGEX = /[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89aAbB][a-f0-9]{3}-[a-f0-9]{12}/;

function isUUID(value:any)
{
    return value && typeof value === 'string' && value.match(UUID_REGEX);
}

/**
 * commute the patch sequence a,b to b,a
 * @param {object} a patch operation
 * @param {object} b patch operation
 */
export function commutePaths(a, b)
{
    // TODO: cases for special paths: '' and '/'
    const left = parse(a.path);
    const right = parse(b.path);
    const prefix = getCommonPathPrefix(left, right);
    const isArray = isArrayPath(left, right, prefix.length);

    // Never mutate the originals
    const ac = copyPatch(a);
    const bc = copyPatch(b);

    if( prefix.length === 0 && !isArray )
    // Paths share no common ancestor, simple swap
        return [bc, ac];

    if( isArray )
    {
        /**
         * MintData-specific: we use this approach to keep UUID arrays unique.
         * 
         * Protect "rebase" operation for cases where multiple clients
         * are adding the same box to the same parent's children array.
         * 
         * This code should NOT be triggered for other arrays
         * where non-unique elements are allowed, like colWidths.
         */ 
        if( a.op === 'remove' && b.op === 'add' && isUUID(a.value) && a.value === b.value )
            return [{op:'skip'}, {op:'skip'}];
        /**
         * Default behavior
         */
        else
            return commuteArrayPaths(ac, left, bc, right);
    }
    else
        return commuteTreePaths(ac, left, bc, right);
};

function commuteTreePaths(a, left, b, right)
{
    if( a.path === b.path )
        throw new TypeError('cannot commute ' + a.op + ',' + b.op + ' with identical object paths');

    // FIXME: Implement tree path commutation
    return [b, a];
}

/**
 * Commute two patches whose common ancestor (which may be the immediate parent)
 * is an array
 * @param a
 * @param left
 * @param b
 * @param right
 * @returns {*}
 */
function commuteArrayPaths(a, left, b, right)
{
    if( left.length === right.length )
        return commuteArraySiblings(a, left, b, right);

    if( left.length > right.length )
    {
        // left is longer, commute by "moving" it to the right
        left = commuteArrayAncestor(b, right, a, left, -1);
        a.path = absolute(join(left));
    }
    else
    {
        // right is longer, commute by "moving" it to the left
        right = commuteArrayAncestor(a, left, b, right, 1);
        b.path = absolute(join(right));
    }

    return [b, a];
}

function isArrayPath(left, right, index)
{
    return isValidArrayIndex(left[index])
        && isValidArrayIndex(right[index]);
}

/**
 * Commute two patches referring to items in the same array
 * @param l
 * @param lpath
 * @param r
 * @param rpath
 * @returns {*[]}
 */
function commuteArraySiblings(l, lpath, r, rpath)
{

    const target = lpath.length - 1;
    const lindex = +lpath[target];
    const rindex = +rpath[target];

    let commuted;

    if( lindex < rindex || (lindex === rindex && l.op === 'remove') )
    {
        // Adjust right path
        if( l.op === 'add' || l.op === 'copy' )
        {
            commuted = rpath.slice();
            commuted[target] = Math.max(0, rindex - 1);
            r.path = absolute(join(commuted));
        }
        else if( l.op === 'remove' )
        {
            commuted = rpath.slice();
            commuted[target] = rindex + 1;
            r.path = absolute(join(commuted));
        }
    }
    else if( r.op === 'add' || r.op === 'copy' )
    {
        // Adjust left path
        commuted = lpath.slice();
        commuted[target] = lindex + 1;
        l.path = absolute(join(commuted));
    }
    else if( lindex > rindex && r.op === 'remove' )
    {
        // Adjust left path only if remove was at a (strictly) lower index
        commuted = lpath.slice();
        commuted[target] = Math.max(0, lindex - 1);
        l.path = absolute(join(commuted));
    }

    return [r, l];
}

/**
 * Commute two patches with a common array ancestor
 * @param l
 * @param lpath
 * @param r
 * @param rpath
 * @param direction
 * @returns {*}
 */
function commuteArrayAncestor(l, lpath, r, rpath, direction)
{
    // rpath is longer or same length

    const target = lpath.length - 1;
    const lindex = +lpath[target];
    const rindex = +rpath[target];

    // Copy rpath, then adjust its array index
    const rc = rpath.slice();

    if( lindex > rindex )
        return rc;

    if( l.op === 'add' || l.op === 'copy' )
        rc[target] = Math.max(0, rindex - direction);
    else if( l.op === 'remove' )
        rc[target] = Math.max(0, rindex + direction);

    return rc;
}

function getCommonPathPrefix(p1, p2)
{
    const p1l = p1.length;
    const p2l = p2.length;

    if( p1l === 0 || p2l === 0 || (p1l < 2 && p2l < 2) )
        return [];

    // If paths are same length, the last segment cannot be part
    // of a common prefix.  If not the same length, the prefix cannot
    // be longer than the shorter path.
    const l = p1l === p2l
        ? p1l - 1
        : Math.min(p1l, p2l);

    let i = 0;

    while( i < l && p1[i] === p2[i] )
        ++i;

    return p1.slice(0, i);
}

function copyPatch(p)
{
    const copy:any = {op: p.op, path: p.path};

    if( p.context !== void 0 ) copy.context = p.context;

    if( p.op === 'remove' )
    {
        // Nothing else to add
    }
    else if( p.op === 'copy' || p.op === 'move' )
        copy.from = p.from;
    else
    // test, add, replace
        copy.value = p.value;

    return copy;
}

